import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RemoveDoneIcon from "@mui/icons-material/RemoveDone";
import { Bell } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import apiService, { BaseURL } from "../service/Api";
import { RootState } from "../store/Index";
import { getStatusColors } from "../constants/statusColors";
import AssignmentReminderDialog from "../pages/crew-management/components/AssignmentReminderDialog";
import CrewAssignmentDialog from "../pages/crew-management/components/CrewAssignmentDialog";
import ProducerAssignmentDialog from "../pages/crew-management/components/ProducerAssignmentDialog";
import AssignSalesRepModal from "../pages/bookings/view-booking2/components/AssignSalesRepModal";
import { assignSalesRep } from "../pages/bookings/view-booking2/components/salesRepStorage";
import {
  showSuccessToast,
  showErrorToast,
  showInfoToast,
} from "../components/common/ShowToast";
import { tokens } from "../theme";

interface RelatedObjectData {
  type?: string;
  status?: string;
  stream_id?: number;
  id?: number;
  image?: string;
  name?: string;
  venue_image?: string;
  stream?: {
    id?: number;
    booking?: { deceased?: { person_image?: string | null } };
  };
  booking?: { deceased?: { person_image?: string | null } };
  campaign_id?: number;
  reference_id?: string;
  sales_rep?: { id: number; full_name: string } | null;
}

interface NotificationApiItem {
  id: number;
  title: string;
  description: string;
  trigger_source: string;
  status: "read" | "unread" | "archived";
  object_id?: number;
  related_object_data?: RelatedObjectData;
  created_by_full_name: string;
  created_at: string;
}

interface NotificationItem {
  id: string;
  title: string;
  description: string;
  trigger_source: string;
  trigger_app: string;
  status: "read" | "unread" | "archived";
  record_id: number;
  timestamp: string;
  record_data: {
    type: string;
    status?: string;
    stream_id: number;
    image?: string;
    campaign_id?: number;
    reference_id?: string;
    sales_rep?: { id: number; full_name: string } | null;
  };
}

interface PaginatedResponse {
  results: NotificationApiItem[];
  next: string | null;
}

function mapApiItem(item: NotificationApiItem): NotificationItem {
  const src = item.trigger_source || "";
  return {
    id: String(item.id),
    title: item.title || "Notification",
    description: item.description || "",
    trigger_source: src,
    trigger_app: src.split(".")[0] || "other",
    status: item.status || "unread",
    record_id: item.object_id ?? 0,
    timestamp: new Date(item.created_at || Date.now()).toLocaleString(),
    record_data: {
      type: item.related_object_data?.type || "unknown",
      status: item.related_object_data?.status,
      stream_id:
        item.related_object_data?.stream_id ??
        item.related_object_data?.stream?.id ??
        item.related_object_data?.id ??
        item.object_id ??
        0,
      image:
        item.related_object_data?.venue_image ||
        item.related_object_data?.booking?.deceased?.person_image ||
        item.related_object_data?.stream?.booking?.deceased?.person_image ||
        undefined,
      campaign_id: item.related_object_data?.campaign_id,
      reference_id: item.related_object_data?.reference_id,
      sales_rep: item.related_object_data?.sales_rep ?? null,
    },
  };
}

function TabLabel({ label, count }: { label: string; count?: number }) {
  const show = count != null && count > 0;
  return (
    <Box display="flex" alignItems="center" gap={0.75} component="span">
      {label}
      {show && (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "error.main",
            color: "#fff",
            borderRadius: "10px",
            px: 0.75,
            minWidth: 20,
            height: 18,
            fontSize: "0.6rem",
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: 0,
          }}
        >
          {count! > 99 ? "99+" : count}
        </Box>
      )}
    </Box>
  );
}

const TAB_PRIORITY = ["booking", "stream", "venue"];

interface NotificationBellProps {
  iconButtonSx?: SxProps<Theme>;
}

export default function NotificationBell({
  iconButtonSx,
}: NotificationBellProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const statusColors = getStatusColors(theme.palette.mode);
  const navigate = useNavigate();
  const accessToken = useSelector((state: RootState) => state.auth.access_token);

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"unread" | "read" | "all">(
    "unread",
  );
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [triggerApps, setTriggerApps] = useState<string[]>([]);
  const [countsByApp, setCountsByApp] = useState<{
    total: number;
    by_app: Record<string, number>;
  }>({ total: 0, by_app: {} });
  const [costAnalysisAlert, setCostAnalysisAlert] =
    useState<NotificationItem | null>(null);
  const [approvingCostAnalysisReopen, setApprovingCostAnalysisReopen] =
    useState(false);
  const [quoteRequestAlert, setQuoteRequestAlert] = useState<{
    id: string;
    title: string;
    description: string;
    record_id: number;
  } | null>(null);

  // Dialog state
  const [selectedNotification, setSelectedNotification] = useState<
    number | null
  >(null);
  const [selectedStream, setSelectedStream] = useState<number | null>(null);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showProducerDialog, setShowProducerDialog] = useState(false);
  const [showCrewDialog, setShowCrewDialog] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null,
  );
  const [showAssignSalesRepDialog, setShowAssignSalesRepDialog] =
    useState(false);
  const [assignRepNotificationId, setAssignRepNotificationId] = useState<
    string | null
  >(null);

  const handleAssignSalesRep = async (salesRepId: number) => {
    if (!selectedBookingId) return;
    try {
      const rep = await assignSalesRep(selectedBookingId, salesRepId);
      showSuccessToast(`${rep.full_name} assigned as sales rep.`);

      if (assignRepNotificationId) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === assignRepNotificationId
              ? {
                  ...n,
                  status: "read",
                  record_data: { ...n.record_data, sales_rep: rep },
                }
              : n,
          ),
        );
        handleMarkRead(assignRepNotificationId);
      }
    } catch {
      showErrorToast("Failed to assign sales rep. Please try again.");
    }
  };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiService.get<{ unread_count: number }>(
        "/api/notifications/notification/unread-count/",
      );
      setUnreadCount(res.data.unread_count);
    } catch {
      // silently ignore
    }
  }, []);

  const fetchCountsByApp = useCallback(async () => {
    try {
      const res = await apiService.get<{
        total: number;
        unread_count?: number;
        by_app: Record<string, number>;
        trigger_apps?: string[];
      }>("/api/notifications/notification/unread-counts-by-app/");
      setCountsByApp(res.data);
      if (res.data.unread_count != null) {
        setUnreadCount(res.data.unread_count);
      } else if (res.data.total != null) {
        setUnreadCount(res.data.total);
      }
      if (res.data.trigger_apps && res.data.trigger_apps.length > 0) {
        setTriggerApps(res.data.trigger_apps);
      }
    } catch {
      // silently ignore
    }
  }, []);

  const fetchTriggerApps = useCallback(async () => {
    try {
      const res = await apiService.get<{ trigger_sources: string[] }>(
        "/api/notifications/notification/trigger-sources/",
      );
      const apps = [
        ...new Set(
          (res.data.trigger_sources || [])
            .map((src) => src.split(".")[0])
            .filter(Boolean),
        ),
      ];
      setTriggerApps(apps);
    } catch (err) {
      console.error("Failed to fetch trigger sources:", err);
    }
  }, []);

  useEffect(() => {
    const refreshNotificationSummary = () => {
      void fetchCountsByApp();
    };

    refreshNotificationSummary();
    const intervalId = window.setInterval(refreshNotificationSummary, 30_000);
    return () => window.clearInterval(intervalId);
  }, [fetchCountsByApp]);

  const buildUrl = useCallback(
    (tab: string, filter: "unread" | "read" | "all") => {
      const params = new URLSearchParams();
      if (tab !== "all") params.set("trigger_app", tab);
      if (filter !== "all") params.set("status", filter);
      const qs = params.toString();
      return `/api/notifications/notification/${qs ? `?${qs}` : ""}`;
    },
    [],
  );

  const fetchNotifications = useCallback(
    async (tab: string, filter: "unread" | "read" | "all") => {
      setLoading(true);
      setNotifications([]);
      setNextPageUrl(null);
      try {
        const res = await apiService.get<PaginatedResponse>(
          buildUrl(tab, filter),
        );
        const mapped = (res.data.results || []).map(mapApiItem);
        setNotifications(mapped);
        setNextPageUrl(res.data.next || null);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      } finally {
        setLoading(false);
      }
    },
    [buildUrl],
  );

  useEffect(() => {
    if (!open) return;
    fetchNotifications(activeTab, statusFilter);
  }, [open, activeTab, statusFilter, fetchNotifications]);

  useEffect(() => {
    if (!accessToken) return;
    fetchCountsByApp();
  }, [accessToken, fetchCountsByApp]);

  const openRef = useRef(open);
  const activeTabRef = useRef(activeTab);
  const statusFilterRef = useRef(statusFilter);
  useEffect(() => {
    openRef.current = open;
    activeTabRef.current = activeTab;
    statusFilterRef.current = statusFilter;
  }, [open, activeTab, statusFilter]);

  const maybeShowQuoteRequestAlert = useCallback(
    (item: {
      id?: number;
      object_id?: number | null;
      title?: string;
      description?: string;
    }) => {
      if (item.id == null) return;
      if (!item.title?.toLowerCase().includes("new booking quote request")) {
        return;
      }
      const notificationId = String(item.id);
      let alreadyDismissed = false;
      try {
        alreadyDismissed = !!sessionStorage.getItem(
          `dismissed-quote-request-notification-${notificationId}`,
        );
      } catch {
        // If session storage is unavailable, just show it.
      }
      if (alreadyDismissed) return;
      setQuoteRequestAlert({
        id: notificationId,
        title: item.title,
        description: item.description || "",
        record_id: item.object_id ?? 0,
      });
    },
    [],
  );

  // Live push: new notifications arrive over ws/notifications/ without waiting
  // for the drawer to be opened or a poll interval to tick.
  useEffect(() => {
    if (!accessToken) return;

    const wsProtocol = BaseURL.startsWith("https") ? "wss" : "ws";
    const wsHost = new URL(BaseURL).host;
    const wsUrl = `${wsProtocol}://${wsHost}/ws/notifications/?token=${accessToken}`;
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      let data: {
        type?: string;
        id?: number;
        object_id?: number | null;
        title?: string;
        description?: string;
        notifications?: Array<{
          id?: number;
          object_id?: number | null;
          title?: string;
          description?: string;
        }>;
      };
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.type === "unread_notifications") {
        (data.notifications || []).forEach(maybeShowQuoteRequestAlert);
        return;
      }

      const isLivePush =
        data.type === "new_notification" ||
        (!!data.title && data.notifications === undefined);
      if (!isLivePush) return;

      setUnreadCount((prev) => prev + 1);
      fetchCountsByApp();
      if (data.title) {
        showInfoToast(`${data.title}${data.description ? ` ${data.description}` : ""}`);
      }
      if (openRef.current) {
        fetchNotifications(activeTabRef.current, statusFilterRef.current);
      }

      maybeShowQuoteRequestAlert(data);
    };

    return () => ws.close();
  }, [accessToken, fetchCountsByApp, fetchNotifications, maybeShowQuoteRequestAlert]);

  const loadMore = async () => {
    if (!nextPageUrl) return;
    setLoadingMore(true);
    try {
      const res = await apiService.get<PaginatedResponse>(nextPageUrl);
      const mapped = (res.data.results || []).map(mapApiItem);
      setNotifications((prev) => [...prev, ...mapped]);
      setNextPageUrl(res.data.next || null);
    } catch (err) {
      console.error("Failed to load more notifications:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await apiService.post(
        "/api/notifications/notification/mark-all-read/",
        {},
      );
      await fetchNotifications(activeTab, statusFilter);
      fetchUnreadCount();
      fetchCountsByApp();
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleMarkAllUnread = async () => {
    setMarkingAll(true);
    try {
      await apiService.post(
        "/api/notifications/notification/mark-all-unread/",
        {},
      );
      await fetchNotifications(activeTab, statusFilter);
      fetchUnreadCount();
      fetchCountsByApp();
    } catch (err) {
      console.error("Failed to mark all as unread:", err);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await apiService.patch(
        `/api/notifications/notification/${id}/mark-read/`,
        {},
      );
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: "read" } : n)),
      );
      fetchUnreadCount();
      fetchCountsByApp();
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  };

  const handleMarkUnread = async (id: string) => {
    try {
      await apiService.patch(
        `/api/notifications/notification/${id}/mark-unread/`,
        {},
      );
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: "unread" } : n)),
      );
      fetchUnreadCount();
      fetchCountsByApp();
    } catch (err) {
      console.error("Failed to mark as unread:", err);
    }
  };

  const extractBookingId = (text?: string): number | null => {
    if (!text) return null;
    const match = text.match(/booking\s*#(\d+)/i);
    return match ? Number(match[1]) : null;
  };

  const getNotificationLink = (n: NotificationItem): string | undefined => {
    if (n.trigger_source === "job_career.application_submitted") {
      return n.record_id ? `/jobs?application=${n.record_id}` : "/jobs";
    }
    if (n.trigger_source === "chema.identity_verification") {
      return `/chema?tab=verification`;
    }
    if (n.trigger_source === "chema.withdrawal") {
      return `/chema/${n.record_data.campaign_id}`;
    }
    if (!n.record_id) return undefined;
    const t = n.title.toLowerCase();

    if (n.trigger_source === "cost_analysis.costanalysis") {
      const needsProducerAttention =
        t.includes("approved") ||
        t.includes("rejected") ||
        t.includes("reopened") ||
        t.includes("denied");
      if (needsProducerAttention) {
        // Creator's copy: send them back to the stream, not the finance queue.
        const streamId = n.record_data.stream_id || n.record_id;
        return `/stream-management/view/${streamId}/cost-analysis`;
      }
      // Finance's copy: "submitted for review" etc. still goes to the queue.
      return `/pending-approval?case=${n.record_id}`;
    }
    if (t.includes("streamfund application")) {
      return `/streamfund/applications/${n.record_data.reference_id}`;
    }
    if (t.includes("sponsorship stream"))
      return `/sponsorships/view/${n.record_id}`;
    if (t.includes("stream")) {
      return n.record_data.type === "call_sheet"
        ? `/stream-management/view/${n.record_data.stream_id}`
        : `/stream-management/view/${n.record_id}`;
    }
    if (t.includes("quote")) return `/request-for-quote/${n.record_id}`;
    if (t.includes("booking")) return `/bookings/${n.record_id}`;
    if (t.includes("venue")) return `/venues/view/${n.record_id}`;
    if (t.includes("referral")) return `/referrals/view/${n.record_id}`;
    if (t.includes("workflow"))
      return `/memory-table/view/${n.record_data.stream_id}`;
    return undefined;
  };

  const dismissCostAnalysisAlert = useCallback((notificationId: string) => {
    try {
      sessionStorage.setItem(
        `dismissed-cost-analysis-notification-${notificationId}`,
        "1",
      );
    } catch {
      // The popup can still be dismissed when session storage is unavailable.
    }
    setCostAnalysisAlert(null);
  }, []);

  const dismissQuoteRequestAlert = useCallback((notificationId: string) => {
    try {
      sessionStorage.setItem(
        `dismissed-quote-request-notification-${notificationId}`,
        "1",
      );
    } catch {
      // The popup can still be dismissed when session storage is unavailable.
    }
    setQuoteRequestAlert(null);
  }, []);

  const handleViewQuoteRequestAlert = () => {
    if (!quoteRequestAlert) return;
    const alertToView = quoteRequestAlert;
    dismissQuoteRequestAlert(alertToView.id);
    handleMarkRead(alertToView.id);
    navigate(`/request-for-quote/${alertToView.record_id}`);
  };

  const fetchCostAnalysisAttention = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        trigger_app: "cost_analysis",
        status: "unread",
      });
      const res = await apiService.get<PaginatedResponse>(
        `/api/notifications/notification/?${params.toString()}`,
      );
      const nextAlert = (res.data.results || [])
        .map(mapApiItem)
        .find((notification) => {
          const title = notification.title.toLowerCase();
          const isPendingReview =
            notification.record_data.status === "awaiting_audit" &&
            (title.includes("pending review") ||
              title.includes("assigned to you"));
          const isReopenRequest =
            notification.record_data.status === "reopen_requested" &&
            title.includes("reopening requested");
          const isActionableFinanceAlert =
            isPendingReview || isReopenRequest;
          if (!isActionableFinanceAlert) return false;
          try {
            return !sessionStorage.getItem(
              `dismissed-cost-analysis-notification-${notification.id}`,
            );
          } catch {
            return true;
          }
        });
      setCostAnalysisAlert((current) => current || nextAlert || null);
    } catch {
      // Attention popups must never interfere with the existing bell.
    }
  }, []);

  useEffect(() => {
    void fetchCostAnalysisAttention();
    const intervalId = window.setInterval(fetchCostAnalysisAttention, 45_000);
    return () => window.clearInterval(intervalId);
  }, [fetchCostAnalysisAttention]);

  const handleViewCostAnalysisAlert = async () => {
    if (!costAnalysisAlert) return;
    const notification = costAnalysisAlert;
    const link = getNotificationLink(notification);
    dismissCostAnalysisAlert(notification.id);
    await handleMarkRead(notification.id);
    if (link) navigate(link);
  };

  const handleApproveCostAnalysisReopen = async () => {
    if (!costAnalysisAlert?.record_id) return;
    const notification = costAnalysisAlert;
    setApprovingCostAnalysisReopen(true);
    try {
      await apiService.post(
        `/api/cost-analysis/cost-analyses/${notification.record_id}/approve-reopen/`,
        {},
      );
      await handleMarkRead(notification.id);
      dismissCostAnalysisAlert(notification.id);
      showSuccessToast("Reopening approved. The cost analysis is now a draft.");
    } catch {
      showErrorToast("Failed to approve the reopening request.");
    } finally {
      setApprovingCostAnalysisReopen(false);
    }
  };

  const tabs = useMemo(() => {
    const sorted = [...triggerApps].filter(Boolean).sort((a, b) => {
      const ai = TAB_PRIORITY.indexOf(a);
      const bi = TAB_PRIORITY.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
    const appTabs = sorted.map((app) => ({
      id: app,
      label: app.charAt(0).toUpperCase() + app.slice(1),
    }));
    return [{ id: "all", label: "All" }, ...appTabs];
  }, [triggerApps]);

  const handleOpen = () => {
    setActiveTab("all");
    setStatusFilter("unread");
    setOpen(true);
    fetchCountsByApp();
  };

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          onClick={handleOpen}
          aria-label="Notifications"
          sx={iconButtonSx}
        >
          <Badge badgeContent={unreadCount || undefined} color="error">
            <Bell size={22} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "90%", sm: 440 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" fontWeight="bold" fontSize={22}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {unreadCount} unread
                </Typography>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
              <Tooltip title="Mark all as read" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleMarkAllRead}
                    disabled={markingAll}
                    aria-label="Mark all as read"
                  >
                    {markingAll ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DoneAllIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Mark all as unread" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleMarkAllUnread}
                    disabled={markingAll}
                    aria-label="Mark all as unread"
                  >
                    <RemoveDoneIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                onClick={() => setOpen(false)}
                aria-label="Close notifications"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Status filter chips */}
          <Box display="flex" gap={1} mt={1.5}>
            {(["unread", "all", "read"] as const).map((f) => (
              <Chip
                key={f}
                label={f.charAt(0).toUpperCase() + f.slice(1)}
                size="small"
                clickable
                color={statusFilter === f ? "primary" : "default"}
                variant={statusFilter === f ? "filled" : "outlined"}
                onClick={() => setStatusFilter(f)}
              />
            ))}
          </Box>

          {/* Scrollable tabs with auto chevrons */}
          <Tabs
            value={activeTab}
            onChange={(_, v: string) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              mt: 2,
              borderBottom: 1,
              borderColor: "divider",
              ".MuiTab-root": {
                minWidth: 0,
                px: 1.5,
                fontSize: "0.8rem",
                textTransform: "none",
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={
                  <TabLabel
                    label={tab.label}
                    count={
                      tab.id === "all"
                        ? countsByApp.total
                        : countsByApp.by_app[tab.id]
                    }
                  />
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Notification list */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={32} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box textAlign="center" py={6} color="text.secondary">
              <Bell size={40} style={{ opacity: 0.3, marginBottom: 8 }} />
              <Typography variant="body2">No notifications found.</Typography>
            </Box>
          ) : (
            <AnimatePresence mode="popLayout">
              {notifications.map((n) => {
                const isUnread = n.status === "unread";
                const link = getNotificationLink(n);
                const t = n.title.toLowerCase();

                return (
                  <motion.div
                    key={n.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        mb: 1.5,
                        bgcolor: isUnread
                          ? theme.palette.mode === "dark"
                            ? colors.primary[900]
                            : colors.primary[800]
                          : "action.hover",
                        borderLeft: isUnread
                          ? `3px solid ${theme.palette.primary.main}`
                          : "3px solid transparent",
                      }}
                    >
                      <Box display="flex" gap={1.5}>
                        {n.record_data.image && (
                          <Avatar
                            src={n.record_data.image}
                            sx={{ width: 44, height: 44, flexShrink: 0 }}
                          />
                        )}

                        <Box flex={1} minWidth={0}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            gap={1}
                          >
                            <Typography
                              component={link ? "span" : "span"}
                              variant="body2"
                              fontWeight={isUnread ? 700 : 400}
                              onClick={
                                link
                                  ? () => {
                                      if (isUnread) handleMarkRead(n.id);
                                      setOpen(false);
                                      navigate(link);
                                    }
                                  : undefined
                              }
                              sx={{
                                color: link ? "primary.main" : "text.primary",
                                textDecoration: "none",
                                flex: 1,
                                cursor: link ? "pointer" : "default",
                                "&:hover": link
                                  ? { textDecoration: "underline" }
                                  : undefined,
                              }}
                            >
                              {n.title}
                            </Typography>

                            {/* Mark read / unread toggle */}
                            {isUnread ? (
                              <Tooltip title="Mark as read">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkRead(n.id)}
                                  sx={{ flexShrink: 0 }}
                                >
                                  <CheckCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Mark as unread">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkUnread(n.id)}
                                  sx={{ flexShrink: 0 }}
                                >
                                  <RadioButtonUncheckedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>

                          {n.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mt: 0.5, whiteSpace: "pre-line" }}
                            >
                              {n.description}
                            </Typography>
                          )}

                          <Typography
                            variant="caption"
                            color="text.disabled"
                            display="block"
                            mt={0.5}
                          >
                            {n.timestamp}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Action chips */}
                      <Box display="flex" flexWrap="wrap" gap={0.75} mt={1}>
                        {n.record_id && t.includes("payment reminder") && (
                          <Chip
                            component="a"
                            clickable
                            size="small"
                            label="Send Payment Reminder"
                            href={`/bookings/${n.record_id}?paymentReminder=1`}
                            sx={{
                              bgcolor: statusColors.success.bg,
                              color: statusColors.success.fg,
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {n.record_id &&
                          n.record_data.type === "stream" &&
                          !t.includes("quote") && (
                            <Chip
                              component="a"
                              clickable
                              size="small"
                              label="View Booking"
                              href={`/bookings/${extractBookingId(n.description)}`}
                              sx={{
                                bgcolor: statusColors.success.bg,
                                color: statusColors.success.fg,
                                fontWeight: 600,
                              }}
                            />
                          )}

                        {n.record_id &&
                          (t.includes("new stream created") ||
                            t.includes("new producer assignment") ||
                            t.includes("new stream scheduled") ||
                            t.includes("gig accepted") ||
                            t.includes("gig declined")) && (
                            <Chip
                              component="a"
                              clickable
                              size="small"
                              label="View Stream"
                              href={`/stream-management/view/${n.record_data.type === "call_sheet" ? n.record_data.stream_id : n.record_id}`}
                              sx={{
                                bgcolor: statusColors.info.bg,
                                color: statusColors.info.fg,
                                fontWeight: 600,
                              }}
                            />
                          )}

                        {n.record_id &&
                          t.includes("streamfund application") && (
                            <Chip
                              component="a"
                              clickable
                              size="small"
                              label="Assign Decision"
                              href={getNotificationLink(n)}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                bgcolor: statusColors.success.bg,
                                color: statusColors.success.fg,
                                fontWeight: 600,
                              }}
                            />
                          )}

                        {n.record_id &&
                          (t.includes("new stream created") ||
                            t.includes("new stream scheduled")) && (
                            <Chip
                              clickable
                              size="small"
                              label="Assign Producer"
                              onClick={() => {
                                setSelectedStream(n.record_id);
                                setShowProducerDialog(true);
                              }}
                              sx={{
                                bgcolor: statusColors.success.bg,
                                color: statusColors.success.fg,
                                fontWeight: 600,
                              }}
                            />
                          )}

                        {n.record_id &&
                          t.includes("new producer assignment") && (
                            <Chip
                              clickable
                              size="small"
                              label="Assign Crew"
                              onClick={() => {
                                setSelectedStream(n.record_data.stream_id);
                                setShowCrewDialog(true);
                              }}
                              sx={{
                                bgcolor: statusColors.success.bg,
                                color: statusColors.success.fg,
                                fontWeight: 600,
                              }}
                            />
                          )}

                        {n.record_id &&
                          (t.includes("gig accepted") ||
                            t.includes("gig declined")) && (
                            <Chip
                              clickable
                              size="small"
                              label="Assign Crew"
                              onClick={() => {
                                setSelectedStream(n.record_id);
                                setShowCrewDialog(true);
                              }}
                              sx={{
                                bgcolor: statusColors.success.bg,
                                color: statusColors.success.fg,
                                fontWeight: 600,
                              }}
                            />
                          )}

                        {n.record_id &&
                          (t.includes("new stream created") ||
                            t.includes("new producer assignment") ||
                            t.includes("new stream scheduled")) && (
                            <Chip
                              clickable
                              size="small"
                              label="Remind Later"
                              onClick={() => {
                                setSelectedNotification(parseInt(n.id));
                                setSelectedStream(
                                  n.record_data.type === "call_sheet"
                                    ? n.record_data.stream_id
                                    : n.record_id,
                                );
                                setShowReminderDialog(true);
                              }}
                              sx={{
                                bgcolor: statusColors.warning.bg,
                                color: statusColors.warning.fg,
                                fontWeight: 600,
                              }}
                            />
                          )}

                        {n.record_id &&
                          n.trigger_source === "booking.sales_manager_alert" && (
                            <>
                              <Chip
                                clickable
                                size="small"
                                label={
                                  n.record_data.sales_rep
                                    ? "Reassign"
                                    : "Assign Rep"
                                }
                                onClick={() => {
                                  setSelectedBookingId(n.record_id);
                                  setAssignRepNotificationId(n.id);
                                  setShowAssignSalesRepDialog(true);
                                }}
                                sx={{
                                  bgcolor: statusColors.success.bg,
                                  color: statusColors.success.fg,
                                  fontWeight: 600,
                                }}
                              />
                              <Chip
                                component="a"
                                clickable
                                size="small"
                                label="View Booking"
                                href={`/bookings/${n.record_id}`}
                                sx={{
                                  bgcolor: statusColors.info.bg,
                                  color: statusColors.info.fg,
                                  fontWeight: 600,
                                }}
                              />
                            </>
                          )}

                        {n.record_id && n.trigger_source === "booking.booking" && (
                          <Chip
                            component="a"
                            clickable
                            size="small"
                            label="View Booking"
                            href={`/bookings/${n.record_id}`}
                            sx={{
                              bgcolor: statusColors.info.bg,
                              color: statusColors.info.fg,
                              fontWeight: 600,
                            }}
                          />
                        )}

                        {n.trigger_source === "venue.venue" && (
                          <Chip
                            component="a"
                            clickable
                            size="small"
                            label="View Venue"
                            href={`/venues/view/${n.record_id}`}
                            sx={{
                              bgcolor: statusColors.error.bg,
                              color: statusColors.error.fg,
                              fontWeight: 600,
                            }}
                          />
                        )}

                        {n.trigger_source === "cost_analysis.costanalysis" &&
                          (() => {
                            const needsProducerAttention =
                              t.includes("approved") ||
                              t.includes("rejected") ||
                              t.includes("reopened") ||
                              t.includes("denied");
                            const streamId =
                              n.record_data.stream_id || n.record_id;
                            return (
                              <Chip
                                component="a"
                                clickable
                                size="small"
                                label={needsProducerAttention ? "View Cost Analysis" : "View Case"}
                                href={
                                  needsProducerAttention
                                    ? `/stream-management/view/${streamId}/cost-analysis`
                                    : `/pending-approval?case=${n.record_id}`
                                }
                                sx={{
                                  bgcolor: statusColors.success.bg,
                                  color: statusColors.success.fg,
                                  fontWeight: 600,
                                }}
                              />
                            );
                          })()}
                        {n.record_id && t.includes("sponsorship stream") && (
                          <Chip
                            component="a"
                            clickable
                            size="small"
                            label="View Sponsorship"
                            href={`/sponsorships/view/${n.record_id}`}
                            sx={{
                              bgcolor: statusColors.success.bg,
                              color: statusColors.success.fg,
                              fontWeight: 600,
                            }}
                          />
                        )}

                        {n.record_data.campaign_id &&
                          t.includes("withdrawal request") && (
                            <Chip
                              component="a"
                              clickable
                              size="small"
                              label="View Fundraiser"
                              href={`/chema/${n.record_data.campaign_id}`}
                              sx={{
                                bgcolor: statusColors.success.bg,
                                color: statusColors.success.fg,
                                fontWeight: 600,
                              }}
                            />
                          )}

                        {n.record_id && t.includes("new referral") && (
                          <Chip
                            component="a"
                            clickable
                            size="small"
                            label="View Referral"
                            href={`/referrals/view/${n.record_id}`}
                            sx={{
                              bgcolor: statusColors.success.bg,
                              color: statusColors.success.fg,
                              fontWeight: 600,
                            }}
                          />
                        )}

                        {n.record_id &&
                          (t.includes("new booking quote request") ||
                            t.includes("quotation ready")) && (
                            <Chip
                              component="a"
                              clickable
                              size="small"
                              label="Preview Quotation"
                              href={`/request-for-quote/${n.record_id}`}
                              sx={{
                                bgcolor: statusColors.success.bg,
                                color: statusColors.success.fg,
                                fontWeight: 600,
                              }}
                            />
                          )}
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}

          {/* Load more */}
          {nextPageUrl && !loading && (
            <Box textAlign="center" mt={2} mb={2}>
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                variant="outlined"
                size="small"
              >
                {loadingMore ? <CircularProgress size={16} /> : "Load More"}
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      <Dialog
        open={Boolean(costAnalysisAlert)}
        onClose={() =>
          costAnalysisAlert && dismissCostAnalysisAlert(costAnalysisAlert.id)
        }
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cost analysis needs attention</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            {costAnalysisAlert?.title}
          </Typography>
          {costAnalysisAlert?.description && (
            <Typography variant="body2" color="text.secondary">
              {costAnalysisAlert.description}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              costAnalysisAlert &&
              dismissCostAnalysisAlert(costAnalysisAlert.id)
            }
          >
            Dismiss
          </Button>
          {costAnalysisAlert?.title
            .toLowerCase()
            .includes("reopening requested") && (
            <Button
              variant="outlined"
              disabled={approvingCostAnalysisReopen}
              onClick={() => void handleApproveCostAnalysisReopen()}
            >
              {approvingCostAnalysisReopen ? "Approving…" : "Approve reopening"}
            </Button>
          )}
          <Button
            variant="contained"
            disableElevation
            disabled={approvingCostAnalysisReopen}
            onClick={() => void handleViewCostAnalysisAlert()}
          >
            View cost analysis
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(quoteRequestAlert)}
        onClose={() =>
          quoteRequestAlert && dismissQuoteRequestAlert(quoteRequestAlert.id)
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>
          New quote request
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 16 }}>
            {quoteRequestAlert?.title}
          </Typography>
          {quoteRequestAlert?.description && (
            <Typography variant="body1" color="text.secondary">
              {quoteRequestAlert.description}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() =>
              quoteRequestAlert &&
              dismissQuoteRequestAlert(quoteRequestAlert.id)
            }
          >
            Dismiss
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleViewQuoteRequestAlert}
          >
            Preview Quotation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogs */}
      <AssignmentReminderDialog
        notification={selectedNotification}
        setShowAssignmentReminderDialog={setShowReminderDialog}
        showAssignmentReminderDialog={showReminderDialog}
        selectedStream={selectedStream}
      />
      <ProducerAssignmentDialog
        setShowProducerAssignmentDialog={setShowProducerDialog}
        showProducerAssignmentDialog={showProducerDialog}
        selectedStream={selectedStream}
      />
      <CrewAssignmentDialog
        setShowCrewAssignmentDialog={setShowCrewDialog}
        showCrewAssignmentDialog={showCrewDialog}
        selectedStream={selectedStream}
      />
      <AssignSalesRepModal
        open={showAssignSalesRepDialog}
        onClose={() => setShowAssignSalesRepDialog(false)}
        onSubmit={handleAssignSalesRep}
      />
    </>
  );
}
